import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  createExcerptusPrompt,
  updateExcerptusPrompt,
  type ExcerptusPrompt,
} from "@/lib/composerPrompts";

const tagsToString = (t: string[] | null | undefined) => (t ?? []).join(", ");
const stringToTags = (v: string) => v.split(",").map((t) => t.trim()).filter(Boolean);

type Props = {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  prompt: ExcerptusPrompt | null;
  onSaved: () => void;
};

export const PromptEditorDialog = ({ open, onOpenChange, prompt, onSaved }: Props) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [modelHint, setModelHint] = useState("");
  const [slug, setSlug] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [isPublished, setIsPublished] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(prompt?.title ?? "");
    setBody(prompt?.body ?? "");
    setDescription(prompt?.description ?? "");
    setTags(tagsToString(prompt?.tags));
    setModelHint(prompt?.model_hint ?? "");
    setSlug(prompt?.slug ?? "");
    setSortOrder(prompt?.sort_order ?? 0);
    setIsPublished(Boolean(prompt?.is_published));
  }, [open, prompt]);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Title required");
      return;
    }
    if (!body.length) {
      toast.error("Body required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        body,
        description: description.trim() || null,
        tags: stringToTags(tags),
        model_hint: modelHint.trim() || null,
        slug: slug.trim() || null,
        sort_order: sortOrder,
        is_published: isPublished,
      };
      if (prompt) await updateExcerptusPrompt(prompt.id, payload);
      else await createExcerptusPrompt(payload);
      toast.success("Saved");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{prompt ? "Edit Excerptus prompt" : "New Excerptus prompt"}</DialogTitle>
          <DialogDescription>
            Body is preserved verbatim — paste your `.md` prompt as-is.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label>Body</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={16}
              spellCheck={false}
              className="font-mono text-xs leading-relaxed"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label>Tags (comma-separated)</Label>
              <Input value={tags} onChange={(e) => setTags(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Model hint</Label>
              <Input value={modelHint} onChange={(e) => setModelHint(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Slug (optional, unique)</Label>
              <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="long-form-essay-outliner" />
            </div>
            <div className="grid gap-1.5">
              <Label>Sort order</Label>
              <Input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label className="text-sm">Published</Label>
              <p className="text-xs text-muted-foreground">Visible to all users when on.</p>
            </div>
            <Switch checked={isPublished} onCheckedChange={setIsPublished} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
